import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { firstValueFrom } from 'rxjs';
import {
    CameraDto, BarrierDto, DisplayDto, DeviceGroupDto,
    InfrastructureDto,
    ListBarriersRequest, BarrierDtoPaginationResult,
    DisplayDtoPaginationResult, ListCamerasRequest, CameraDtoPaginationResult
} from '../../../../../api/gen';
import { InfrastructureService } from '../../../../../api/gen/api/infrastructure.service';
import { BarrierService } from '../../../../../api/gen/api/barrier.service';
import { DisplayService } from '../../../../../api/gen/api/display.service';
import { CameraService } from '../../../../../api/gen/api/camera.service';

export type AssignDeviceType = 'camera' | 'barrier' | 'display';

export interface AssignExistingDeviceDialogData {
    deviceType: AssignDeviceType;
    currentDeviceGroupId: string;
    infrastructureId: string;
}

export interface AssignExistingDeviceDialogResult {
    deviceId: string;
    deviceName: string;
    deviceType: AssignDeviceType;
}

interface AvailableDevice {
    id: string;
    name: string;
    deviceGroupName: string;
    deviceGroupId: string;
}

@Component({
    selector: 'app-assign-existing-device-dialog',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatListModule,
        MatProgressSpinnerModule,
        MatDividerModule
    ],
    templateUrl: './assign-existing-device-dialog.component.html'
})
export class AssignExistingDeviceDialogComponent implements OnInit {
    availableDevices = signal<AvailableDevice[]>([]);
    selectedDevice = signal<AvailableDevice | null>(null);
    isLoading = signal(true);

    public dialogRef = inject(MatDialogRef<AssignExistingDeviceDialogComponent, AssignExistingDeviceDialogResult | null>);
    public data: AssignExistingDeviceDialogData = inject(MAT_DIALOG_DATA);
    private infrastructureService = inject(InfrastructureService);
    private barrierService = inject(BarrierService);
    private displayService = inject(DisplayService);
    private cameraService = inject(CameraService);

    get deviceTypeLabel(): string {
        switch (this.data.deviceType) {
            case 'camera': return 'Camera';
            case 'barrier': return 'Barrier';
            case 'display': return 'Display';
        }
    }

    get deviceTypeIcon(): string {
        switch (this.data.deviceType) {
            case 'camera': return 'videocam';
            case 'barrier': return 'door_front';
            case 'display': return 'tv';
        }
    }

    async ngOnInit(): Promise<void> {
        this.isLoading.set(true);
        try {
            const infrastructure = await firstValueFrom(
                this.infrastructureService.infrastructuresInfrastructureIdGet(this.data.infrastructureId)
            ) as InfrastructureDto;

            const otherGroups = (infrastructure.deviceGroups || []).filter(
                g => g.id !== this.data.currentDeviceGroupId
            );

            const devices = await this.loadDevicesFromGroups(otherGroups);
            this.availableDevices.set(devices);
        } catch (error) {
            console.error('Error loading available devices:', error);
            this.availableDevices.set([]);
        } finally {
            this.isLoading.set(false);
        }
    }

    private async loadDevicesFromGroups(groups: DeviceGroupDto[]): Promise<AvailableDevice[]> {
        const devices: AvailableDevice[] = [];

        switch (this.data.deviceType) {
            case 'camera': {
                for (const group of groups) {
                    if (!group.id) continue;
                    try {
                        const result = await firstValueFrom(
                            this.cameraService.camerasFilterPost({ deviceGroupId: group.id, pageIndex: 0, pageSize: 100 } as ListCamerasRequest)
                        ) as CameraDtoPaginationResult;
                        for (const cam of result?.items || []) {
                            if (cam.id) {
                                devices.push({
                                    id: cam.id,
                                    name: cam.deviceName || cam.id,
                                    deviceGroupName: group.groupName || group.id || '',
                                    deviceGroupId: group.id
                                });
                            }
                        }
                    } catch {
                        // skip group on error
                    }
                }
                break;
            }
            case 'barrier': {
                for (const group of groups) {
                    if (!group.id) continue;
                    try {
                        const result = await firstValueFrom(
                            this.barrierService.barriersFilterPost({
                                deviceGroupId: group.id,
                                pageNumber: 1,
                                pageSize: 10
                            } as ListBarriersRequest)
                        ) as BarrierDtoPaginationResult;
                        for (const barrier of result?.items || []) {
                            if (barrier.id) {
                                devices.push({
                                    id: barrier.id,
                                    name: barrier.deviceName || barrier.id,
                                    deviceGroupName: group.groupName || group.id || '',
                                    deviceGroupId: group.id
                                });
                            }
                        }
                    } catch {
                        // skip group on error
                    }
                }
                break;
            }
            case 'display': {
                for (const group of groups) {
                    if (!group.id) continue;
                    try {
                        const result = await firstValueFrom(
                            this.displayService.displaysGet(undefined, undefined, group.id, undefined, undefined, 1, 100)
                        ) as DisplayDtoPaginationResult;
                        for (const display of result?.items || []) {
                            if (display.id) {
                                devices.push({
                                    id: display.id,
                                    name: display.deviceName || display.id,
                                    deviceGroupName: group.groupName || group.id || '',
                                    deviceGroupId: group.id
                                });
                            }
                        }
                    } catch {
                        // skip group on error
                    }
                }
                break;
            }
        }

        return devices;
    }

    selectDevice(device: AvailableDevice): void {
        this.selectedDevice.set(device);
    }

    isSelected(device: AvailableDevice): boolean {
        return this.selectedDevice()?.id === device.id;
    }

    confirm(): void {
        const selected = this.selectedDevice();
        if (!selected) return;
        this.dialogRef.close({
            deviceId: selected.id,
            deviceName: selected.name,
            deviceType: this.data.deviceType
        });
    }

    cancel(): void {
        this.dialogRef.close(null);
    }
}
